import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import StatusBadge, { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

const TYPE_LABELS = {
  PERCENTAGE: "Percentage",
  HOURS: "Hours",
  MONTHS: "Months",
  MULTIPLE: "Multiple",
  NUMBER: "Number",
};

function formatValue(def, value) {
  if (value === null || value === undefined) return "—";
  if (def.value_type === "PERCENTAGE") return `${value} %`;
  if (def.value_type === "HOURS") return `${value} h`;
  if (def.value_type === "MONTHS") return `${value} months`;
  if (def.value_type === "MULTIPLE") return `${value}x`;
  return String(value);
}

export default function FinancialParametersPage() {
  const { isAdmin } = useAuth();
  const [defs, setDefs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [values, setValues] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ effective_from: "", effective_to: "", value: "" });

  const load = async (keepSelection = true) => {
    const res = await api.get("/parameters");
    setDefs(res.data);
    if (keepSelection && selected) {
      setSelected(res.data.find((d) => d.id === selected.id) || null);
    } else if (!selected && res.data.length > 0) {
      setSelected(res.data[0]);
    }
  };

  const loadValues = async (id) => {
    const res = await api.get(`/parameters/${id}/values`);
    setValues(res.data);
  };

  useEffect(() => {
    load(false).catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected) loadValues(selected.id).catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const addValue = async () => {
    try {
      await api.post(`/parameters/${selected.id}/values`, {
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
        value: Number(form.value),
      });
      toast.success("Parameter value added");
      setDialogOpen(false);
      setForm({ effective_from: "", effective_to: "", value: "" });
      await loadValues(selected.id);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="financial-parameters-page">
      <PageHeader
        title="Financial Parameters"
        subtitle="Group financial assumptions — effective-dated, never hardcoded in calculation logic"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden lg:col-span-1" data-testid="parameters-list">
          <div className="bg-secondary/50 border-b border-slate-200 py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Parameter definitions
          </div>
          <div>
            {defs.map((d) => (
              <button
                key={d.id}
                onClick={() => setSelected(d)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${
                  selected?.id === d.id ? "bg-[#EBF3FE]" : "hover:bg-accent/40"
                }`}
                data-testid={`parameter-item-${d.code}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold ${selected?.id === d.id ? "text-[#0052CC]" : "text-slate-800"}`}>
                    {d.name}
                  </span>
                  <span className="text-sm font-semibold text-slate-900">{formatValue(d, d.current_value)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-slate-500 font-mono">{d.code}</span>
                  <span className="text-xs text-slate-400">· {TYPE_LABELS[d.value_type]}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selected && (
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="parameter-detail">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{selected.name}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {selected.code} · {TYPE_LABELS[selected.value_type]} · Current value:{" "}
                    <span className="font-semibold text-slate-800">{formatValue(selected, selected.current_value)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <ActiveBadge active={selected.active} testId="parameter-active-badge" />
                  {isAdmin && (
                    <Button
                      onClick={() => setDialogOpen(true)}
                      className="bg-[#0052CC] hover:bg-[#0047B3]"
                      size="sm"
                      data-testid="add-parameter-value-button"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add value
                    </Button>
                  )}
                </div>
              </div>
              <table className="w-full" data-testid="parameter-values-table">
                <thead>
                  <tr className="bg-secondary/50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Effective from</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Effective to</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Value</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {values.map((v) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const current = v.effective_from <= today && (!v.effective_to || v.effective_to >= today);
                    return (
                      <tr key={v.id} className="border-b border-slate-100" data-testid={`parameter-value-row-${v.id}`}>
                        <td className="py-2.5 px-3 text-sm text-slate-700">{v.effective_from}</td>
                        <td className="py-2.5 px-3 text-sm text-slate-700">{v.effective_to || "Open"}</td>
                        <td className="py-2.5 px-3 text-sm font-semibold text-slate-900">{formatValue(selected, v.value)}</td>
                        <td className="py-2.5 px-3">
                          {current ? (
                            <StatusBadge tone="green" testId={`parameter-value-current-${v.id}`}>Current</StatusBadge>
                          ) : v.effective_from > today ? (
                            <StatusBadge tone="blue" testId={`parameter-value-future-${v.id}`}>Future</StatusBadge>
                          ) : (
                            <StatusBadge tone="slate" testId={`parameter-value-historical-${v.id}`}>Historical</StatusBadge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {values.length === 0 && (
                <EmptyState title="No values yet" description="Historical values are preserved — new values never change history." testId="parameter-values-empty" />
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="parameter-value-dialog">
          <DialogHeader>
            <DialogTitle>Add value — {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Value ({selected?.unit || TYPE_LABELS[selected?.value_type]})</Label>
              <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="parameter-value-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Effective from</Label>
                <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="parameter-effective-from-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Effective to (optional)</Label>
                <Input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} data-testid="parameter-effective-to-input" />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Overlapping effective periods for the same parameter are rejected. Existing history is never modified.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="parameter-value-cancel-button">Cancel</Button>
            <Button onClick={addValue} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="parameter-value-save-button">Add value</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
